import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { formatCurrency, formatDate } from '../utils/helpers';
import Loading from '../components/Loading';
import Alert from '../components/Alert';
import Pagination from '../components/Pagination';
import { useAuth } from '../context/AuthContext';
import QuotationPreviewModal from '../components/QuotationPreviewModal';
import { 
  Plus, 
  Search, 
  Eye, 
  Pencil, 
  FileCheck, 
  Mail, 
  Trash2, 
  RotateCcw,
  NotepadText 
} from 'lucide-react';
import './Quotations.css';
import Layout from '../components/Layout';


const getQuotationStatusColor = (status) => {
    const colors = {
        draft: 'secondary',
        sent: 'info',
        accepted: 'success',
        rejected: 'danger',
        converted: 'primary'
    };
    return colors[status] || 'secondary';
};

const Quotations = () => {
    const [quotations, setQuotations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
    const [filters, setFilters] = useState({
        status: '',
        fromDate: '',
        toDate: '',
        search: ''
    });
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [selectedQuotationId, setSelectedQuotationId] = useState(null);
    const searchInputRef = useRef(null);

    const { isAdmin } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        fetchQuotations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.page, filters]);

    const fetchQuotations = async () => {
        try {
            setLoading(true);
            const params = {
                page: pagination.page,
                limit: 20,
                ...filters
            };

            const response = await api.get('/quotations', { params });
            setQuotations(response.data.quotations);
            setPagination(response.data.pagination);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load quotations');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        const wasSearchInput = name === 'search';
        setFilters(prev => ({ ...prev, [name]: value }));
        setPagination(prev => ({ ...prev, page: 1 }));

        // Maintain focus on search input after state update
        if (wasSearchInput && searchInputRef.current) {
            setTimeout(() => searchInputRef.current?.focus(), 0);
        }
    };

    const resetFilters = () => {
        setFilters({
            status: '',
            fromDate: '',
            toDate: '',
            search: ''
        });
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this quotation?')) {
            return;
        }

        try {
            await api.delete(`/quotations/${id}`);
            setSuccess('Quotation deleted successfully');
            fetchQuotations();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete quotation');
        }
    };

    const handleSendEmail = async (id) => {
        if (!window.confirm('Are you sure you want to send this quotation via email to the customer?')) {
            return;
        }

        try {
            setLoading(true);
            await api.post(`/quotations/${id}/send-email`);
            setSuccess('Quotation email sent successfully');
            fetchQuotations();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to send email');
        } finally {
            setLoading(false);
        }
    };

    const handleConvertToInvoice = async (id) => {
        if (!window.confirm('Convert this quotation to invoice?')) {
            return;
        }

        try {
            const response = await api.post(`/quotations/${id}/convert`);
            setSuccess('Quotation converted to invoice successfully');
            navigate(`/invoice/${response.data.invoice._id}`);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to convert quotation');
        }
    };

    const handleEditClick = (quotationId) => {
        navigate(`/quotations/${quotationId}/edit`);
    };

    const handleModalClose = (saved) => {
        setShowPreviewModal(false);
        setSelectedQuotationId(null);
        if (saved) {
            fetchQuotations();
        }
    };

    // if (loading && quotations.length === 0) return <Layout><Loading /></Layout>;

    return (
        <div>
            {showPreviewModal && selectedQuotationId && (
                <QuotationPreviewModal
                    quotationId={selectedQuotationId}
                    onClose={handleModalClose}
                />
            )}

            <div>
                <div>
                    <div className="page-header">
                        <h1 style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <NotepadText size={28} /> Quotations
                        </h1>
                        {true && (
                            <Link
                                to="/quotations/create"
                                className="btn btn-primary"
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Plus size={18} /> Create Quotation
                                </div>
                            </Link>
                        )}
                    </div>

                    {error && <Alert type="error" message={error} onClose={() => setError('')} />}
                    {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

                    {/* Filters */}
                    <div className="card">
                        <div className="card-header">Search & Filters</div>

                        {/* Search Bar */}
                        <div style={{ padding: '15px 20px', borderBottom: '1px solid #e5e7eb' }}>
                            <div className="form-group" style={{ marginBottom: 0, position: 'relative' }}>
                                <Search 
                                    size={18} 
                                    style={{ 
                                        position: 'absolute', 
                                        left: '12px', 
                                        top: '50%', 
                                        transform: 'translateY(-50%)', 
                                        color: '#94a3b8' 
                                    }} 
                                />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    name="search"
                                    className="form-control"
                                    placeholder="Search by quotation number or customer name..."
                                    value={filters.search}
                                    onChange={handleFilterChange}
                                    style={{ fontSize: '15px', paddingLeft: '40px' }}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Status</label>
                                <select name="status" className="form-control" value={filters.status} onChange={handleFilterChange}>
                                    <option value="">All</option>
                                    <option value="draft">Draft</option>
                                    <option value="sent">Sent</option>
                                    <option value="accepted">Accepted</option>
                                    <option value="rejected">Rejected</option>
                                    <option value="converted">Converted to Invoice</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label">From Date</label>
                                <input
                                    type="date"
                                    name="fromDate"
                                    className="form-control"
                                    value={filters.fromDate}
                                    onChange={handleFilterChange}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">To Date</label>
                                <input
                                    type="date"
                                    name="toDate"
                                    className="form-control"
                                    value={filters.toDate}
                                    onChange={handleFilterChange}
                                />
                            </div>
                             <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                                <button className="btn btn-secondary" onClick={resetFilters}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <RotateCcw size={16} /> Reset
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Quotations Table */}
                    <div className="card">
                        <div className="card-header">
                            All Quotations ({pagination.total})
                        </div>
                        <div className="table-container">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Quotation No</th>
                                        <th>Date</th>
                                        <th>Valid Until</th>
                                        <th>Customer</th>
                                        <th>Amount</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {quotations.length > 0 ? (
                                        quotations.map(quotation => (
                                            <tr key={quotation._id}>
                                                <td>{quotation.quotationNumber}</td>
                                                <td>{formatDate(quotation.quotationDate)}</td>
                                                <td>{formatDate(quotation.validUntil)}</td>
                                                <td>{quotation.customerDetails?.name}</td>
                                                <td>{formatCurrency(quotation.grandTotal)}</td>
                                                <td>
                                                    <span className={`badge badge-${getQuotationStatusColor(quotation.status)}`}>
                                                        {quotation.status.toUpperCase()}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'nowrap' }}>
                                                        <Link
                                                            to={`/quotations/${quotation._id}`}
                                                            className="btn btn-primary btn-small"
                                                            style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                                                        >
                                                            <Eye size={14} /> View
                                                        </Link>
                                                        {quotation.status !== 'converted' && (
                                                            <>
                                                                <button
                                                                    onClick={() => handleEditClick(quotation._id)}
                                                                    className="btn btn-secondary btn-small"
                                                                    style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                                                                >
                                                                    <Pencil size={14} /> Edit
                                                                </button>
                                                                <button
                                                                    className="btn btn-success btn-small"
                                                                    onClick={() => handleConvertToInvoice(quotation._id)}
                                                                    style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                                                                >
                                                                    <FileCheck size={14} /> To Invoice
                                                                </button>
                                                                <button
                                                                    className="btn btn-info btn-small"
                                                                    onClick={() => handleSendEmail(quotation._id)}
                                                                    title="Send quotation via email"
                                                                    style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                                                                >
                                                                    <Mail size={14} /> Email
                                                                </button>
                                                                <button
                                                                    className="btn btn-danger btn-small"
                                                                    onClick={() => handleDelete(quotation._id)}
                                                                    style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                                                                >
                                                                    <Trash2 size={14} />
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7" style={{ textAlign: 'center' }}>No quotations found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {pagination.pages > 1 && (
                        <Pagination
                            currentPage={pagination.page}
                            totalPages={pagination.pages}
                            onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default Quotations;

